import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faExclamationCircle,
  faBookOpen,
  faPlus,
  faTimes,
  faTrash,
  faUserGraduate,
  faLayerGroup,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';

interface LearnershipAdmin {
  id: string;
  title: string;
  saqaId: string;
  nqfLevel: number | null;
  credits: number | null;
  durationMonths: number;
  summary: string;
  isActive: boolean;
  moduleCount: number;
  enrolledStudentCount: number;
}

interface ModuleFormRow {
  title: string;
  description: string;
  orderIndex: number;
  moduleType: number; // 0 = Knowledge, 1 = Practical, 2 = Workplace
}

export default function AdminCoursesPage() {
  const [courses, setCourses] = useState<LearnershipAdmin[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState('');

  // Create course form state
  const [title, setTitle] = useState('');
  const [saqaId, setSaqaId] = useState('');
  const [nqfLevel, setNqfLevel] = useState('');
  const [credits, setCredits] = useState('');
  const [durationMonths, setDurationMonths] = useState('12');
  const [summary, setSummary] = useState('');
  const [modules, setModules] = useState<ModuleFormRow[]>([
    { title: '', description: '', orderIndex: 1, moduleType: 0 },
  ]);

  const { accessToken } = useAuth();

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await fetch('/api/admin/learnerships', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Failed to load courses');
      setCourses(await response.json());
    } catch (err) {
      console.error(err);
      setError('Failed to load courses. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setTitle('');
    setSaqaId('');
    setNqfLevel('');
    setCredits('');
    setDurationMonths('12');
    setSummary('');
    setModules([{ title: '', description: '', orderIndex: 1, moduleType: 0 }]);
    setFormError('');
  };

  const addModuleRow = () => {
    setModules((prev) => [...prev, { title: '', description: '', orderIndex: prev.length + 1, moduleType: 0 }]);
  };

  const removeModuleRow = (index: number) => {
    setModules((prev) => prev.filter((_, i) => i !== index));
  };

  const updateModuleRow = (index: number, field: keyof ModuleFormRow, value: string | number) => {
    setModules((prev) =>
      prev.map((m, i) => (i === index ? { ...m, [field]: value } : m))
    );
  };

  const handleCreateCourse = async () => {
    setFormError('');

    if (!title.trim()) {
      setFormError('Course title is required.');
      return;
    }

    const validModules = modules.filter((m) => m.title.trim());

    try {
      setIsSaving(true);
      const response = await fetch('/api/admin/learnerships', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          title,
          saqaId,
          nqfLevel: nqfLevel ? parseInt(nqfLevel, 10) : null,
          credits: credits ? parseInt(credits, 10) : null,
          durationMonths: parseInt(durationMonths, 10) || 12,
          summary,
          modules: validModules.map((m, i) => ({
            title: m.title,
            description: m.description,
            orderIndex: i + 1,
            moduleType: m.moduleType,
          })),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setFormError(data.message || 'Failed to create course.');
        return;
      }

      setShowCreateModal(false);
      resetForm();
      fetchCourses();
    } catch (err) {
      console.error(err);
      setFormError('An error occurred while creating the course.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      {/* Top Bar */}
      <div className="bg-white border-b border-canvas-300 px-8 py-4 sticky top-0 z-10 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-brand-950">Courses</h1>
          <p className="text-sm text-brand-900/60 mt-1">Manage the learnership catalog</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-brand-gradient text-white font-semibold rounded-lg hover:shadow-lg transition-all flex items-center gap-2 text-sm"
        >
          <FontAwesomeIcon icon={faPlus} className="h-3.5 w-3.5" />
          New Course
        </button>
      </div>

      <div className="p-8">
        {isLoading && (
          <div className="flex items-center justify-center py-24">
            <FontAwesomeIcon icon={faSpinner} className="h-10 w-10 text-brand-600 animate-spin" />
          </div>
        )}

        {!isLoading && error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
            <FontAwesomeIcon icon={faExclamationCircle} className="h-8 w-8 text-red-600 mb-3" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {!isLoading && !error && courses.length === 0 && (
          <div className="card-surface p-12 text-center">
            <FontAwesomeIcon icon={faBookOpen} className="h-12 w-12 text-brand-300 mb-4" />
            <h3 className="text-lg font-bold text-brand-950">No courses yet</h3>
            <p className="text-sm text-brand-900/60 mt-2">
              Create your first learnership to start enrolling students.
            </p>
          </div>
        )}

        {!isLoading && !error && courses.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {courses.map((course) => (
              <div key={course.id} className="card-surface p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-11 h-11 rounded-2xl bg-brand-gradient flex items-center justify-center text-white flex-shrink-0">
                      <FontAwesomeIcon icon={faBookOpen} className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-bold text-brand-950">{course.title}</h3>
                      <p className="text-xs text-brand-900/50 mt-1">
                        SAQA {course.saqaId || '—'}
                        {course.nqfLevel ? ` · NQF ${course.nqfLevel}` : ''}
                      </p>
                    </div>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                      course.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {course.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>

                {course.summary && (
                  <p className="text-sm text-brand-900/60 mt-4 line-clamp-2">{course.summary}</p>
                )}

                <div className="mt-5 grid grid-cols-3 gap-2 border-t border-canvas-200 pt-4 text-xs">
                  <div>
                    <p className="text-brand-900/45 flex items-center gap-1">
                      <FontAwesomeIcon icon={faLayerGroup} className="h-3 w-3" /> Modules
                    </p>
                    <p className="font-semibold text-brand-950 mt-1">{course.moduleCount}</p>
                  </div>
                  <div>
                    <p className="text-brand-900/45 flex items-center gap-1">
                      <FontAwesomeIcon icon={faUserGraduate} className="h-3 w-3" /> Enrolled
                    </p>
                    <p className="font-semibold text-brand-950 mt-1">{course.enrolledStudentCount}</p>
                  </div>
                  <div>
                    <p className="text-brand-900/45">Duration</p>
                    <p className="font-semibold text-brand-950 mt-1">{course.durationMonths} mo</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Course Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-brand-950/40 z-50 flex items-center justify-center px-4 py-8 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-2xl p-6 my-auto max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-brand-950">New Course</h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="p-1.5 hover:bg-canvas-100 rounded-lg text-brand-900/60"
              >
                <FontAwesomeIcon icon={faTimes} className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-brand-950 mb-2">
                  Course Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. IT Technical Support"
                  className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-brand-950 mb-2">SAQA ID</label>
                  <input
                    type="text"
                    value={saqaId}
                    onChange={(e) => setSaqaId(e.target.value)}
                    placeholder="78964"
                    className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-brand-950 mb-2">NQF Level</label>
                  <input
                    type="number"
                    value={nqfLevel}
                    onChange={(e) => setNqfLevel(e.target.value)}
                    placeholder="4"
                    className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-brand-950 mb-2">Credits</label>
                  <input
                    type="number"
                    value={credits}
                    onChange={(e) => setCredits(e.target.value)}
                    placeholder="163"
                    className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-brand-950 mb-2">Duration (months)</label>
                <input
                  type="number"
                  value={durationMonths}
                  onChange={(e) => setDurationMonths(e.target.value)}
                  className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-brand-950 mb-2">Summary</label>
                <textarea
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  rows={2}
                  placeholder="Brief description of this learnership..."
                  className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>

              {/* Modules */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-semibold text-brand-950">Modules</label>
                  <button
                    onClick={addModuleRow}
                    className="text-xs font-semibold text-brand-700 hover:text-brand-800 flex items-center gap-1"
                  >
                    <FontAwesomeIcon icon={faPlus} className="h-3 w-3" />
                    Add module
                  </button>
                </div>
                <div className="space-y-2">
                  {modules.map((module, index) => (
                    <div key={index} className="flex gap-2 items-start">
                      <span className="text-xs font-semibold text-brand-900/40 mt-2.5 w-5">
                        {index + 1}.
                      </span>
                      <input
                        type="text"
                        value={module.title}
                        onChange={(e) => updateModuleRow(index, 'title', e.target.value)}
                        placeholder="Module title"
                        className="flex-1 px-3 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                      />
                      <select
                        value={module.moduleType}
                        onChange={(e) => updateModuleRow(index, 'moduleType', parseInt(e.target.value, 10))}
                        className="px-2 py-2 border border-canvas-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
                        title="QCTO module type"
                      >
                        <option value={0}>Knowledge</option>
                        <option value={1}>Practical</option>
                        <option value={2}>Workplace</option>
                      </select>
                      {modules.length > 1 && (
                        <button
                          onClick={() => removeModuleRow(index)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg flex-shrink-0"
                        >
                          <FontAwesomeIcon icon={faTrash} className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {formError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                  <FontAwesomeIcon icon={faExclamationCircle} className="h-4 w-4 text-red-700 flex-shrink-0" />
                  <p className="text-sm text-red-700">{formError}</p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="px-5 py-2 border border-canvas-300 text-brand-950 font-semibold rounded-lg hover:bg-canvas-100 transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateCourse}
                disabled={isSaving}
                className="px-5 py-2 bg-brand-gradient text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 text-sm flex items-center gap-2"
              >
                <FontAwesomeIcon
                  icon={isSaving ? faSpinner : faPlus}
                  className={`h-3.5 w-3.5 ${isSaving ? 'animate-spin' : ''}`}
                />
                {isSaving ? 'Creating...' : 'Create Course'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
